"use client";

import { useLayoutEffect, useRef, useState } from "react";

// Deliberately memory-only: route changes and drawer remounts retain position;
// a full browser reload starts fresh. Each navigation tree has its own position.
const positions = new Map<string, number>();

export function useNavigationScroll(key: string, pathname: string) {
  const [viewport, setViewport] = useState<HTMLDivElement | null>(null);
  const previousNavigation = useRef<{
    viewport: HTMLDivElement;
    pathname: string;
  } | null>(null);
  useLayoutEffect(() => {
    if (!viewport) return;
    const target = positions.get(key) ?? 0;
    viewport.scrollTop = target;
    let restoring = viewport.scrollTop !== target;
    const restore = () => {
      if (!restoring) return;
      viewport.scrollTop = target;
      restoring = viewport.scrollTop !== target;
    };
    const save = () => {
      if (!restoring) positions.set(key, viewport.scrollTop);
    };
    const interact = () => {
      restoring = false;
    };
    const observer = new ResizeObserver(restore);
    if (viewport.firstElementChild)
      observer.observe(viewport.firstElementChild);
    viewport.addEventListener("scroll", save, { passive: true });
    viewport.addEventListener("wheel", interact, { passive: true });
    viewport.addEventListener("touchstart", interact, { passive: true });
    viewport.addEventListener("keydown", interact);
    return () => {
      // The outgoing DOM may already have shrunk during a tree swap. Keep
      // the last tracked position rather than saving its clamped scrollTop.
      observer.disconnect();
      viewport.removeEventListener("scroll", save);
      viewport.removeEventListener("wheel", interact);
      viewport.removeEventListener("touchstart", interact);
      viewport.removeEventListener("keydown", interact);
    };
  }, [key, viewport]);
  useLayoutEffect(() => {
    if (!viewport) return;
    const previous = previousNavigation.current;
    const animate =
      previous?.viewport === viewport && previous.pathname !== pathname;
    previousNavigation.current = { viewport, pathname };
    let destination: number | undefined;
    let cancelled = false;
    const revealCurrentPage = () => {
      if (cancelled) return;
      const active = viewport.querySelector<HTMLElement>(
        '[aria-current="page"]',
      );
      if (!active || active.closest('[inert], [aria-hidden="true"]')) return;
      const bounds = viewport.getBoundingClientRect();
      const item = active.getBoundingClientRect();
      if (!bounds.height || !item.height) return;
      const inset = 16;
      const top = Math.max(0, bounds.top + viewport.clientTop) + inset;
      const bottom =
        Math.min(
          window.innerHeight,
          bounds.top + viewport.clientTop + viewport.clientHeight,
        ) - inset;
      // Adjust only this sidebar, never the document or another scroll area.
      const delta =
        item.top < top
          ? item.top - top
          : item.bottom > bottom
            ? item.bottom - bottom
            : 0;
      if (delta) {
        const top = Math.max(0, viewport.scrollTop + delta);
        // Resize notifications can repeat during branch expansion. Do not
        // restart an in-flight animation when its destination is unchanged.
        if (destination !== undefined && Math.abs(destination - top) < 1)
          return;
        destination = top;
        if (
          animate &&
          !window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ) {
          viewport.scrollTo({ top, behavior: "smooth" });
        } else {
          viewport.scrollTop = top;
          positions.set(key, viewport.scrollTop);
        }
      }
    };
    const stop = () => {
      cancelled = true;
    };
    // Ancestors may expand after navigation, or items arrive with account data.
    const resize = new ResizeObserver(revealCurrentPage);
    resize.observe(viewport);
    const observeItems = () => {
      for (const child of viewport.children) resize.observe(child);
      revealCurrentPage();
    };
    const mutations = new MutationObserver(observeItems);
    mutations.observe(viewport, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-current", "aria-hidden", "inert"],
    });
    observeItems();
    viewport.addEventListener("wheel", stop, { passive: true });
    viewport.addEventListener("touchstart", stop, { passive: true });
    viewport.addEventListener("pointerdown", stop, { passive: true });
    viewport.addEventListener("keydown", stop);
    return () => {
      resize.disconnect();
      mutations.disconnect();
      viewport.removeEventListener("wheel", stop);
      viewport.removeEventListener("touchstart", stop);
      viewport.removeEventListener("pointerdown", stop);
      viewport.removeEventListener("keydown", stop);
    };
  }, [key, pathname, viewport]);
  return setViewport;
}
